import type { ProjectType } from "@acme/types";
import { imageCdnFullWidthProps } from "@acme/ui/image-cdn";
import { Link } from "@tanstack/react-router";
import { Image } from "@unpic/react";
import * as m from "~/paraglide/messages.js";
import ProjectPlaceholder from "./project-placeholder";
import { ProjectStatusTag } from "./project-status-tag";

interface ProjectCardProps {
  project: ProjectType;
}

/**
 * One project in the grid: a screenshot cropped to its top edge, then the
 * title, its status and a sentence.
 *
 * The whole tile is a single link rather than a tile with a link inside it, so
 * a keyboard reaches each project once.
 */
export default function ProjectCard({ project }: Readonly<ProjectCardProps>) {
  const { title, description, slug, imageUrl, status } = project;
  const isConcept = status === "concept";

  return (
    <Link
      className="project-tile"
      params={{ projectId: slug }}
      to="/projects/$projectId"
    >
      <div className="project-tile-media">
        {imageUrl ? (
          <Image
            alt=""
            layout="fullWidth"
            sizes="(max-width: 720px) 100vw, 50vw"
            src={imageUrl}
            {...imageCdnFullWidthProps(imageUrl)}
          />
        ) : (
          <ProjectPlaceholder title={title} />
        )}
      </div>

      <div className="project-tile-copy">
        <div className="project-tile-head">
          <h2>{title}</h2>
          <ProjectStatusTag status={status} />
        </div>
        {description ? <p>{description}</p> : null}
        <span className="read-link">
          {isConcept ? m.projects_explore() : m.projects_view()}{" "}
          <span aria-hidden="true">↗</span>
        </span>
      </div>
    </Link>
  );
}
